/**
 * Sources Page - Manage knowledge sources (notes and Google Drive)
 * Redesigned with card layout, auto-open picker after OAuth, and post count dialog
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../platform';
import {
  Plus,
  FileText,
  RefreshCw,
  Trash2,
  Edit2,
  CheckCircle,
  Sparkles,
  Loader2,
  X,
  ArrowLeft,
  MoreHorizontal,
} from 'lucide-react';
import { api, streamPipelineEvents, type Source, type PipelineProgress, type TldvMeeting, type PickerItem, type GenerationContext } from '../lib/api';
import { GoogleDrivePicker } from '../components/GoogleDrivePicker';
import { TldvMeetingPicker } from '../components/TldvMeetingPicker';
import { IntegrationItemPicker } from '../components/IntegrationItemPicker';
import { WelcomeHero } from '../components/WelcomeHero';
import { clearCacheKey } from '../lib/request-cache';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
} from '@powercore/ui';
import {
  ConnectorModal,
  type IntegrationConfig,
  NotionLogo,
  FathomLogo,
  GongLogo,
  FirefliesLogo,
  OtterLogo,
  AvomaLogo,
  GranolaLogo,
  GoogleMeetLogo,
  ZoomLogo,
} from '../components/ConnectorModal';

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
}

// Brand SVG logos
function GoogleDriveLogo({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA"/>
      <path d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L3.45 44.6c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47"/>
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l6.85 12.5 6.9 11.3z" fill="#EA4335"/>
      <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D"/>
      <path d="M59.8 49H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.5c1.6 0 3.15-.45 4.5-1.2L59.8 49z" fill="#2684FC"/>
      <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 24h27.5c0-1.55-.4-3.1-1.2-4.5l-12.7-18z" fill="#FFBA00"/>
    </svg>
  );
}

function TldvLogo({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/logo-tldv.png" alt="tl;dv" className={`${className} rounded-full`} />;
}

// Source type display metadata — maps DB type to display name and logo component
const SOURCE_TYPE_META: Record<string, { label: string; icon: (cls: string) => React.ReactNode }> = {
  google_drive: { label: 'Google Drive', icon: (cls) => <GoogleDriveLogo className={cls} /> },
  google_meet:  { label: 'Google Meet',  icon: (cls) => <GoogleMeetLogo className={cls} /> },
  tldv:         { label: 'tl;dv',        icon: (cls) => <TldvLogo className={cls} /> },
  custom_note:  { label: 'Notes',        icon: (cls) => <FileText className={cls + ' text-gray-600'} /> },
  notion:       { label: 'Notion',       icon: (cls) => <NotionLogo className={cls} /> },
  fathom:       { label: 'Fathom',       icon: (cls) => <FathomLogo className={cls} /> },
  gong:         { label: 'Gong',         icon: (cls) => <GongLogo className={cls} /> },
  fireflies:    { label: 'Fireflies.ai', icon: (cls) => <FirefliesLogo className={cls} /> },
  otter:        { label: 'Otter.ai',     icon: (cls) => <OtterLogo className={cls} /> },
  avoma:        { label: 'Avoma',        icon: (cls) => <AvomaLogo className={cls} /> },
  granola:      { label: 'Granola',     icon: (cls) => <GranolaLogo className={cls} /> },
  zoom:         { label: 'Zoom',        icon: (cls) => <ZoomLogo className={cls} /> },
};

function getSourceLabel(type: string): string {
  return SOURCE_TYPE_META[type]?.label || type;
}

function getSourceIcon(type: string, className: string): React.ReactNode {
  const meta = SOURCE_TYPE_META[type];
  if (meta) return meta.icon(className);
  return <FileText className={className + ' text-gray-600'} />;
}

export function SourcesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingSource, setEditingSource] = useState<Source | null>(null);
  const [syncing, setSyncing] = useState<Set<string>>(new Set());
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [showMeetDrivePicker, setShowMeetDrivePicker] = useState(false);
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [generatingPosts, setGeneratingPosts] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [connectingGoogleMeet, setConnectingGoogleMeet] = useState(false);
  const [connectingGranola, setConnectingGranola] = useState(false);
  const [connectingOtter, setConnectingOtter] = useState(false);

  // tldv state
  const [showTldvApiKeyInput, setShowTldvApiKeyInput] = useState(false);
  const [tldvApiKey, setTldvApiKey] = useState('');
  const [connectingTldv, setConnectingTldv] = useState(false);
  const [showTldvPicker, setShowTldvPicker] = useState(false);

  // Generic integration connection (API key-based)
  const [pendingIntegration, setPendingIntegration] = useState<IntegrationConfig | null>(null);
  const [integrationApiKey, setIntegrationApiKey] = useState('');
  const [integrationApiSecret, setIntegrationApiSecret] = useState('');
  const [connectingIntegration, setConnectingIntegration] = useState(false);

  // Integration item picker (Notion/Fathom/Gong/Fireflies/Avoma)
  const [showIntegrationPicker, setShowIntegrationPicker] = useState(false);
  const [activePickerSourceType, setActivePickerSourceType] = useState<string | null>(null);

  // Tracks "connect in progress" — shows spinner instead of stale source list until the new source appears
  const [waitingForIntegration, setWaitingForIntegration] = useState(() => {
    return sessionStorage.getItem('mazo_pendingIntegrationPicker') !== null;
  });

  // Source drill-down: filter documents by connection type
  const [selectedSourceType, setSelectedSourceType] = useState<string | null>(null);

  // Change 2: Auto-open picker after OAuth
  const [autoOpenPicker, setAutoOpenPicker] = useState(false);
  const [autoOpenMeetPicker, setAutoOpenMeetPicker] = useState(false);

  // Post count selection dialog — restore from sessionStorage to survive auth-triggered remounts
  const [showPostCountDialog, setShowPostCountDialog] = useState(() => {
    return sessionStorage.getItem('mazo_pendingPostCount') !== null;
  });
  const [pendingFileCount, setPendingFileCount] = useState(() => {
    return parseInt(sessionStorage.getItem('mazo_pendingPostCount') || '0', 10);
  });

  // Source selection for selective generation — persisted in sessionStorage to survive auth remounts
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>(() => {
    const stored = sessionStorage.getItem('mazo_pendingSourceIds');
    return stored ? JSON.parse(stored) : [];
  });
  const [useSelectedSources, setUseSelectedSources] = useState(() => {
    return sessionStorage.getItem('mazo_pendingSourceIds') !== null;
  });

  // Source item name for filtering to a specific document/meeting within a source
  const [pendingSourceItemName, setPendingSourceItemName] = useState<string | null>(() => {
    return sessionStorage.getItem('mazo_pendingSourceItemName');
  });

  // Generation context — perspective, language, and additional instructions
  const [generationPerspective, setGenerationPerspective] = useState('');
  const [generationLanguage, setGenerationLanguage] = useState('en');
  const [generationAdditionalContext, setGenerationAdditionalContext] = useState('');

  // Source content preview
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Inline document selection checkboxes
  const [checkedDocKeys, setCheckedDocKeys] = useState<Set<string>>(new Set());

  // Selection mode toggle — checkboxes only visible when active
  const [selectionMode, setSelectionMode] = useState(false);

  // More actions dropdown — tracks which row's menu is open
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  // Pipeline progress state
  const [progressPhase, setProgressPhase] = useState<PipelineProgress['phase'] | null>(null);
  const [progressData, setProgressData] = useState<PipelineProgress | null>(null);
  const cancelStreamRef = useRef<(() => void) | null>(null);

  // Form state for new/edit note
  const [noteName, setNoteName] = useState('');
  const [noteContent, setNoteContent] = useState('');

  // Ref to track sync in progress (prevents double calls from StrictMode)
  const syncInProgressRef = useRef<Set<string>>(new Set());

  // Check for Google Drive, Google Meet, and tldv sources
  const googleDriveSource = sources.find(s => s.type === 'google_drive');
  const googleMeetSource = sources.find(s => s.type === 'google_meet');
  const tldvSource = sources.find(s => s.type === 'tldv');

  const loadSources = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.listSources();
      if (result.data) {
        // Filter out sources with pending deletes (survive auth-triggered remounts)
        const pendingDeletes = JSON.parse(sessionStorage.getItem('mazo_pendingDeletes') || '[]') as string[];
        const filtered = pendingDeletes.length > 0
          ? result.data.data.filter(s => !pendingDeletes.includes(s.id))
          : result.data.data;
        setSources(filtered);
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    }
    setLoading(false);
  }, []);

  // Restore connecting state after auth-triggered remount (prevents flicker).
  // Only restore if the page loaded very recently (within 5s) — stale flags from
  // abandoned OAuth flows or page refreshes should be cleared.
  useEffect(() => {
    const flag = sessionStorage.getItem('mazo_connectingGoogle');
    if (flag) {
      const ts = parseInt(flag, 10);
      const isRecent = !isNaN(ts) && Date.now() - ts < 5000;
      if (isRecent) {
        setConnectingGoogle(true);
        setShowConnectorModal(true);
      } else {
        sessionStorage.removeItem('mazo_connectingGoogle');
      }
    }
    const meetFlag = sessionStorage.getItem('mazo_connectingGoogleMeet');
    if (meetFlag) {
      const ts = parseInt(meetFlag, 10);
      const isRecent = !isNaN(ts) && Date.now() - ts < 5000;
      if (isRecent) {
        setConnectingGoogleMeet(true);
        setShowConnectorModal(true);
      } else {
        sessionStorage.removeItem('mazo_connectingGoogleMeet');
      }
    }
  }, []);

  // Cleanup SSE stream on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (cancelStreamRef.current) {
        cancelStreamRef.current();
        cancelStreamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadSources();
    }
  }, [isAuthenticated, loadSources]);

  // Check URL params for post-onboarding redirect, Google connection success, or errors
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      setShowConnectorModal(true);
    }
    if (params.get('google') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      sessionStorage.removeItem('mazo_connectingGoogle');
      setAutoOpenPicker(true);
      loadSources();
    }
    if (params.get('google_meet') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      sessionStorage.removeItem('mazo_connectingGoogleMeet');
      setAutoOpenMeetPicker(true);
      loadSources();
    }
    if (params.get('granola') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      setConnectingGranola(false);
      // Auto-open integration picker after Granola OAuth
      sessionStorage.setItem('mazo_pendingIntegrationPicker', 'granola');
      setWaitingForIntegration(true);
      loadSources();
    }
    if (params.get('otter') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      setConnectingOtter(false);
      // Auto-open integration picker after Otter OAuth
      sessionStorage.setItem('mazo_pendingIntegrationPicker', 'otter');
      setWaitingForIntegration(true);
      loadSources();
    }
    const errorParam = params.get('error');
    if (errorParam) {
      window.history.replaceState({}, '', window.location.pathname);
      sessionStorage.removeItem('mazo_connectingGoogle');
      sessionStorage.removeItem('mazo_connectingGoogleMeet');
      setConnectingGoogle(false);
      setConnectingGoogleMeet(false);
      setConnectingGranola(false);
      setConnectingOtter(false);
      const errorMessages: Record<string, string> = {
        google_auth_failed: 'Google Drive connection failed. Please try again.',
        granola_auth_failed: 'Granola connection failed. Please try again.',
        otter_auth_failed: 'Otter.ai connection failed. Please try again.',
        no_token: 'Google did not return an access token. Please try again.',
        missing_params: 'Authentication was incomplete. Please try again.',
        invalid_state: 'Authentication session expired. Please try again.',
        database_error: 'Failed to save connection. Please try again.',
        source_already_linked: 'This account is already linked to another Mazo account.',
      };
      setError(errorMessages[errorParam] || 'Something went wrong connecting your source. Please try again.');
    }
  }, [loadSources]);

  // Change 2: Auto-open Drive picker when sources load after OAuth connect
  useEffect(() => {
    if (autoOpenPicker && !loading && sources.length > 0) {
      const gdSource = sources.find(s => s.type === 'google_drive');
      if (gdSource) {
        setAutoOpenPicker(false);
        setShowDrivePicker(true);
      }
    }
  }, [autoOpenPicker, loading, sources]);

  // Auto-open Meet Drive picker when sources load after Google Meet OAuth connect
  useEffect(() => {
    if (autoOpenMeetPicker && !loading && sources.length > 0) {
      const meetSource = sources.find(s => s.type === 'google_meet');
      if (meetSource) {
        setAutoOpenMeetPicker(false);
        setShowMeetDrivePicker(true);
      }
    }
  }, [autoOpenMeetPicker, loading, sources]);

  // Auto-open integration picker after connect (survives auth-triggered remounts).
  // The connect POST may still be in-flight when the remounted component loads sources,
  // so retry loadSources() until the source appears. waitingForIntegration keeps the
  // spinner visible instead of flashing the stale source list.
  const retryCountRef = useRef(0);
  useEffect(() => {
    const pendingType = sessionStorage.getItem('mazo_pendingIntegrationPicker');
    if (!pendingType || loading) return;

    const source = sources.find(s => s.type === pendingType);
    if (source) {
      // Source found — stop waiting, open picker
      retryCountRef.current = 0;
      sessionStorage.removeItem('mazo_pendingIntegrationPicker');
      setWaitingForIntegration(false);
      setActivePickerSourceType(pendingType);
      setShowIntegrationPicker(true);
    } else if (retryCountRef.current >= 15) {
      // Safety: stop retrying after ~12s to prevent infinite loop
      retryCountRef.current = 0;
      sessionStorage.removeItem('mazo_pendingIntegrationPicker');
      setWaitingForIntegration(false);
      setError('Source connection timed out. Please try again.');
    } else {
      // Source not in list yet (connect POST likely still in-flight) — retry soon
      retryCountRef.current += 1;
      const timer = setTimeout(() => loadSources(), 800);
      return () => clearTimeout(timer);
    }
  }, [loading, sources, loadSources]);

  const handleCreateNote = async () => {
    if (!noteName.trim()) return;

    const result = await api.createSource({
      type: 'custom_note',
      name: noteName.trim(),
      content: noteContent,
    });

    if (result.data) {
      // Add directly from API response — avoids auth-remount race from a second fetch
      setSources(prev => [...prev, result.data!.data]);
      setShowAddNote(false);
      setNoteName('');
      setNoteContent('');
    } else if (result.error) {
      if (result.error.code === 'SOURCE_ALREADY_LINKED') {
        setError('This source is already connected to another Mazo account. Each source can only be linked to one account. Contact support@getmazo.ai if you need an exception.');
      } else {
        setError(result.error.message);
      }
    }
  };

  const handleUpdateNote = async () => {
    if (!editingSource || !noteName.trim()) return;

    const result = await api.updateSource(editingSource.id, {
      name: noteName.trim(),
      content: noteContent,
    });

    if (result.data) {
      // Update directly from API response — avoids auth-remount race from a second fetch
      setSources(prev => prev.map(s => s.id === editingSource.id ? result.data!.data : s));
      setEditingSource(null);
      setNoteName('');
      setNoteContent('');
    } else if (result.error) {
      setError(result.error.message);
    }
  };

  const handleDeleteSource = async (id: string) => {
    if (!confirm('Are you sure you want to delete this source?')) return;

    // Persist pending delete in sessionStorage to survive auth-triggered remounts
    const pendingDeletes = JSON.parse(sessionStorage.getItem('mazo_pendingDeletes') || '[]') as string[];
    if (!pendingDeletes.includes(id)) {
      pendingDeletes.push(id);
      sessionStorage.setItem('mazo_pendingDeletes', JSON.stringify(pendingDeletes));
    }

    setSources(prev => prev.filter(s => s.id !== id));

    const result = await api.deleteSource(id);

    // Clear this ID from pending deletes
    const remaining = (JSON.parse(sessionStorage.getItem('mazo_pendingDeletes') || '[]') as string[]).filter(d => d !== id);
    if (remaining.length > 0) {
      sessionStorage.setItem('mazo_pendingDeletes', JSON.stringify(remaining));
    } else {
      sessionStorage.removeItem('mazo_pendingDeletes');
    }

    if (result.error) {
      setError(result.error.message);
      loadSources(); // Re-fetch to restore actual state
    }
  };

  const handleSync = async (id: string) => {
    if (syncInProgressRef.current.has(id)) return;
    syncInProgressRef.current.add(id);
    setSyncing(prev => new Set(prev).add(id));

    try {
      const result = await api.syncSource(id);

      if (result.data) {
        setSources(prev => {
          const updated = prev.map(s => {
            if (s.id !== id) return s;
            const serverSource = result.data!.data;
            const localConfig = (s.config || {}) as Record<string, unknown>;
            const serverConfig = (serverSource.config || {}) as Record<string, unknown>;
            const mergedSelectedFiles = serverConfig.selectedFiles || localConfig.selectedFiles;
            return {
              ...serverSource,
              config: {
                ...localConfig,
                ...serverConfig,
                selectedFiles: mergedSelectedFiles,
              },
            };
          });
          return updated;
        });
      } else if (result.error) {
        setError(result.error.message);
      }
    } finally {
      syncInProgressRef.current.delete(id);
      setSyncing(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleConnectGoogleDrive = async () => {
    setConnectingGoogle(true);
    setShowConnectorModal(true);
    // Persist timestamp across auth-triggered remounts so modal stays visible during redirect
    sessionStorage.setItem('mazo_connectingGoogle', String(Date.now()));
    const result = await api.getGoogleAuthUrl();
    if (result.data?.data?.authUrl) {
      window.location.href = result.data.data.authUrl;
    } else if (result.error) {
      setError(result.error.message);
      setConnectingGoogle(false);
      sessionStorage.removeItem('mazo_connectingGoogle');
    }
  };

  const handleConnectGoogleMeet = async () => {
    setConnectingGoogleMeet(true);
    setShowConnectorModal(true);
    sessionStorage.setItem('mazo_connectingGoogleMeet', String(Date.now()));
    const result = await api.getGoogleAuthUrl('google_meet');
    if (result.data?.data?.authUrl) {
      window.location.href = result.data.data.authUrl;
    } else if (result.error) {
      setError(result.error.message);
      setConnectingGoogleMeet(false);
      sessionStorage.removeItem('mazo_connectingGoogleMeet');
    }
  };

  const handleConnectGranolaOAuth = async () => {
    setConnectingGranola(true);
    setShowConnectorModal(true);
    const result = await api.getGranolaAuthUrl();
    if (result.data?.data?.authUrl) {
      window.location.href = result.data.data.authUrl;
    } else {
      setError(result.error?.message || 'Failed to connect to Granola. Please try again.');
      setConnectingGranola(false);
    }
  };

  const handleConnectOtterOAuth = async () => {
    setConnectingOtter(true);
    setShowConnectorModal(true);
    const result = await api.getOtterAuthUrl();
    if (result.data?.data?.authUrl) {
      window.location.href = result.data.data.authUrl;
    } else {
      setError(result.error?.message || 'Failed to connect to Otter.ai. Please try again.');
      setConnectingOtter(false);
    }
  };

  const handleConnectTldv = async () => {
    if (!tldvApiKey.trim()) return;
    setConnectingTldv(true);
    setError(null);

    const result = await api.connectTldv(tldvApiKey.trim());
    if (result.data?.data) {
      // Add the new source to the local state
      setSources(prev => {
        const existing = prev.find(s => s.type === 'tldv');
        if (existing) {
          return prev.map(s => s.type === 'tldv' ? result.data!.data : s);
        }
        return [result.data!.data, ...prev];
      });
      setShowTldvApiKeyInput(false);
      setTldvApiKey('');
      setShowTldvPicker(true);
    } else if (result.error) {
      if (result.error.code === 'SOURCE_ALREADY_LINKED') {
        setError('This source is already connected to another Mazo account. Each source can only be linked to one account. Contact support@getmazo.ai if you need an exception.');
      } else {
        setError(result.error.message);
      }
    }
    setConnectingTldv(false);
  };

  const handleConnectIntegration = (config: IntegrationConfig) => {
    setShowConnectorModal(false);
    setPendingIntegration(config);
    setIntegrationApiKey('');
    setIntegrationApiSecret('');
  };

  const handleSubmitIntegration = async () => {
    if (!pendingIntegration || !integrationApiKey.trim()) return;
    if (pendingIntegration.secondKeyLabel && !integrationApiSecret.trim()) return;
    setConnectingIntegration(true);
    setError(null);

    const credentials: Record<string, string> = { api_key: integrationApiKey.trim() };
    if (pendingIntegration.secondKeyLabel && integrationApiSecret.trim()) {
      credentials.api_secret = integrationApiSecret.trim();
    }

    const integrationType = pendingIntegration.type;

    // Persist picker intent BEFORE async call — survives auth-triggered remounts
    sessionStorage.setItem('mazo_pendingIntegrationPicker', integrationType);
    setWaitingForIntegration(true);
    setPendingIntegration(null);

    const result = await api.connectIntegration(integrationType, credentials);

    if (result.data?.data) {
      setIntegrationApiKey('');
      setIntegrationApiSecret('');
      // Refresh sources from server — the useEffect watching sources will auto-open the picker
      await loadSources();
    } else if (result.error) {
      sessionStorage.removeItem('mazo_pendingIntegrationPicker');
      setWaitingForIntegration(false);
      if (result.error.code === 'SOURCE_ALREADY_LINKED') {
        setError('This source is already connected to another Mazo account. Each source can only be linked to one account. Contact support@getmazo.ai if you need an exception.');
      } else {
        setError(result.error.message);
      }
    }
    setConnectingIntegration(false);
  };

  const handleSaveTldvSelection = async (selectedMeetings: TldvMeeting[]) => {
    setShowTldvPicker(false);

    // Update local source state
    setSources(prev => prev.map(s => {
      if (s.type !== 'tldv') return s;
      const currentConfig = (s.config || {}) as Record<string, unknown>;
      return {
        ...s,
        config: { ...currentConfig, selectedMeetings },
      };
    }));

    if (selectedMeetings.length > 0) {
      // Scope generation to this source only
      const tldvSrc = sources.find(s => s.type === 'tldv');
      const sourceIds = tldvSrc ? [tldvSrc.id] : [];
      setUseSelectedSources(true);
      setSelectedSourceIds(sourceIds);
      sessionStorage.setItem('mazo_pendingSourceIds', JSON.stringify(sourceIds));
      setPendingSourceItemName(null);
      sessionStorage.removeItem('mazo_pendingSourceItemName');

      sessionStorage.setItem('mazo_pendingPostCount', String(selectedMeetings.length));
      setPendingFileCount(selectedMeetings.length);
      setShowPostCountDialog(true);
    }

    // Save to server in background
    try {
      const result = await api.saveTldvSelected(selectedMeetings);
      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save selection');
    }
  };

  const handleDisconnectTldv = async () => {
    if (!confirm('Are you sure you want to disconnect tl;dv?')) return;

    const tldv = sources.find(s => s.type === 'tldv');
    if (tldv) {
      setSources(prev => prev.filter(s => s.type !== 'tldv'));
    }

    const result = await api.disconnectTldv();
    if (result.error) {
      setError(result.error.message);
      loadSources();
    }
  };

  const handleDisconnectGoogleMeet = async () => {
    if (!confirm('Are you sure you want to disconnect Google Meet?')) return;

    const meet = sources.find(s => s.type === 'google_meet');
    if (meet) {
      setSources(prev => prev.filter(s => s.type !== 'google_meet'));
    }

    const result = await api.disconnectGoogleMeet();
    if (result.error) {
      setError(result.error.message);
      loadSources();
    }
  };

  const getSelectedMeetingsCount = (source: Source) => {
    const config = source.config as { selectedMeetings?: TldvMeeting[] } | undefined;
    return config?.selectedMeetings?.length || 0;
  };

  const getSelectedItemsCount = (source: Source) => {
    const config = source.config as { selectedItems?: PickerItem[] } | undefined;
    return config?.selectedItems?.length || 0;
  };

  // Per-type picker config
  const PICKER_CONFIG: Record<string, { title: string; itemLabel: string; actionLabel: string }> = {
    notion: { title: 'Select Notion Pages', itemLabel: 'page', actionLabel: 'Select Pages' },
    fathom: { title: 'Select Fathom Meetings', itemLabel: 'meeting', actionLabel: 'Select Meetings' },
    gong: { title: 'Select Gong Calls', itemLabel: 'call', actionLabel: 'Select Calls' },
    fireflies: { title: 'Select Fireflies Meetings', itemLabel: 'meeting', actionLabel: 'Select Meetings' },
    otter: { title: 'Select Otter.ai Meetings', itemLabel: 'meeting', actionLabel: 'Select Meetings' },
    avoma: { title: 'Select Avoma Meetings', itemLabel: 'meeting', actionLabel: 'Select Meetings' },
    granola: { title: 'Select Granola Notes', itemLabel: 'note', actionLabel: 'Select Notes' },
  };

  const handleSaveIntegrationSelection = async (items: PickerItem[]) => {
    if (!activePickerSourceType) return;
    setShowIntegrationPicker(false);

    // Update local source state
    setSources(prev => prev.map(s => {
      if (s.type !== activePickerSourceType) return s;
      const currentConfig = (s.config || {}) as Record<string, unknown>;
      return { ...s, config: { ...currentConfig, selectedItems: items } };
    }));

    if (items.length > 0) {
      // Scope generation to this source only
      const integrationSrc = sources.find(s => s.type === activePickerSourceType);
      const sourceIds = integrationSrc ? [integrationSrc.id] : [];
      setUseSelectedSources(true);
      setSelectedSourceIds(sourceIds);
      sessionStorage.setItem('mazo_pendingSourceIds', JSON.stringify(sourceIds));
      setPendingSourceItemName(null);
      sessionStorage.removeItem('mazo_pendingSourceItemName');

      sessionStorage.setItem('mazo_pendingPostCount', String(items.length));
      setPendingFileCount(items.length);
      setShowPostCountDialog(true);
    }

    // Save to server in background
    try {
      const result = await api.saveIntegrationSelected(activePickerSourceType, items);
      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save selection');
    }

    setActivePickerSourceType(null);
  };

  const handleDisconnectIntegration = async (type: string) => {
    if (!confirm(`Are you sure you want to disconnect ${SOURCE_TYPE_META[type]?.label || type}?`)) return;
    setSources(prev => prev.filter(s => s.type !== type));
    const result = await api.disconnectIntegration(type);
    if (result.error) {
      setError(result.error.message);
      loadSources();
    }
  };

  const handlePreviewSource = async (sourceId: string, name: string, itemName?: string) => {
    setPreviewTitle(itemName || name);
    setPreviewContent(null);
    setPreviewLoading(true);
    const result = await api.getSourcePreview(sourceId, itemName);
    if (result.data) {
      const data = (result.data as Record<string, unknown>).data as { content: string; source_name: string } | undefined;
      setPreviewContent(data?.content || 'No content available.');
      if (!itemName && data?.source_name) setPreviewTitle(data.source_name);
    } else {
      setPreviewContent('Failed to load preview.');
    }
    setPreviewLoading(false);
  };

  // Change 3: After saving drive selection, show post count dialog instead of auto-generating
  const handleSaveDriveSelection = async (selectedFiles: DriveFile[]) => {
    console.log('[SourcesPage] handleSaveDriveSelection called, files:', selectedFiles.length);

    // Close picker, update sources, and show count dialog — all batched in one render
    setSources(prev => {
      const hasGD = prev.some(s => s.type === 'google_drive');
      console.log('[SourcesPage] setSources updater: hasGoogleDrive=', hasGD, 'sourcesCount=', prev.length);
      return prev.map(s => {
        if (s.type !== 'google_drive') return s;
        const currentConfig = (s.config || {}) as Record<string, unknown>;
        return {
          ...s,
          config: {
            ...currentConfig,
            selectedFiles,
          },
        };
      });
    });
    setShowDrivePicker(false);

    if (selectedFiles.length > 0) {
      // Scope generation to this source only
      const gdSrc = sources.find(s => s.type === 'google_drive');
      const sourceIds = gdSrc ? [gdSrc.id] : [];
      setUseSelectedSources(true);
      setSelectedSourceIds(sourceIds);
      sessionStorage.setItem('mazo_pendingSourceIds', JSON.stringify(sourceIds));
      setPendingSourceItemName(null);
      sessionStorage.removeItem('mazo_pendingSourceItemName');

      // Persist to sessionStorage so the dialog survives auth-triggered remounts
      sessionStorage.setItem('mazo_pendingPostCount', String(selectedFiles.length));
      setPendingFileCount(selectedFiles.length);
      setShowPostCountDialog(true);
      console.log('[SourcesPage] Set showPostCountDialog=true, pendingFileCount=', selectedFiles.length);
    }

    // Save to server in background (sync happens later in handlePostCountSelect)
    try {
      const result = await api.saveGoogleDriveSelected(selectedFiles);
      console.log('[SourcesPage] saveGoogleDriveSelected result:', result.error ? 'ERROR: ' + result.error.message : 'OK');
      if (result.error) {
        if (result.error.code === 'SOURCE_ALREADY_LINKED') {
          setError('This source is already connected to another Mazo account. Each source can only be linked to one account. Contact support@getmazo.ai if you need an exception.');
        } else {
          setError(result.error.message);
        }
      }
    } catch (err) {
      console.error('[SourcesPage] saveGoogleDriveSelected threw:', err);
      setError(err instanceof Error ? err.message : 'Failed to save selection');
    }
  };

  // Change 3b: After saving Meet drive selection, show post count dialog
  const handleSaveMeetDriveSelection = async (selectedFiles: DriveFile[]) => {
    setSources(prev => prev.map(s => {
      if (s.type !== 'google_meet') return s;
      const currentConfig = (s.config || {}) as Record<string, unknown>;
      return { ...s, config: { ...currentConfig, selectedFiles } };
    }));
    setShowMeetDrivePicker(false);

    if (selectedFiles.length > 0) {
      const meetSrc = sources.find(s => s.type === 'google_meet');
      const sourceIds = meetSrc ? [meetSrc.id] : [];
      setUseSelectedSources(true);
      setSelectedSourceIds(sourceIds);
      sessionStorage.setItem('mazo_pendingSourceIds', JSON.stringify(sourceIds));
      setPendingSourceItemName(null);
      sessionStorage.removeItem('mazo_pendingSourceItemName');

      sessionStorage.setItem('mazo_pendingPostCount', String(selectedFiles.length));
      setPendingFileCount(selectedFiles.length);
      setShowPostCountDialog(true);
    }

    try {
      const result = await api.saveGoogleDriveSelected(selectedFiles, 'google_meet');
      if (result.error) {
        if (result.error.code === 'SOURCE_ALREADY_LINKED') {
          setError('This source is already connected to another Mazo account. Each source can only be linked to one account. Contact support@getmazo.ai if you need an exception.');
        } else {
          setError(result.error.message);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save selection');
    }
  };

  // Handle post count selection — try async pipeline first, fall back to synchronous
  const handlePostCountSelect = async (count: number, sourceIds?: string[]) => {
    // Capture source_item_name before clearing state
    const sourceItemName = pendingSourceItemName || undefined;

    sessionStorage.removeItem('mazo_pendingPostCount');
    sessionStorage.removeItem('mazo_pendingSourceIds');
    sessionStorage.removeItem('mazo_pendingSourceItemName');
    setShowPostCountDialog(false);
    setUseSelectedSources(false);
    setSelectedSourceIds([]);
    setPendingSourceItemName(null);

    // Build context from form state
    const languageInstruction = generationLanguage === 'original'
      ? undefined
      : `Write the post in English regardless of the source language.`;
    const context: GenerationContext | undefined =
      (generationPerspective || generationAdditionalContext || languageInstruction)
        ? {
            perspective: generationPerspective || undefined,
            additionalContext: [languageInstruction, generationAdditionalContext].filter(Boolean).join('\n') || undefined,
          }
        : undefined;

    // Reset context form for next time
    setGenerationPerspective('');
    setGenerationLanguage('en');
    setGenerationAdditionalContext('');

    flushSync(() => {
      setGeneratingPosts(true);
      setError(null);
      setProgressPhase(null);
      setProgressData(null);
    });

    await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)));

    // Try async pipeline first
    try {
      const pipelineResult = await api.startPipeline(count, sourceIds, context, sourceItemName);

      if (pipelineResult.data?.pipelineId) {
        // Pipeline started — stream progress via SSE
        const pipelineId = pipelineResult.data.pipelineId;

        const cancel = await streamPipelineEvents(
          pipelineId,
          (progress: PipelineProgress) => {
            setProgressPhase(progress.phase);
            setProgressData(progress);

            if (progress.phase === 'complete') {
              cancelStreamRef.current = null;
              clearCacheKey('posts-list');
              clearCacheKey('posts-stats');
              sessionStorage.setItem('mazo_postsGeneratedAt', String(Date.now()));
              navigate('/');
            } else if (progress.phase === 'failed') {
              cancelStreamRef.current = null;
              setError(progress.error || 'Pipeline failed');
              setGeneratingPosts(false);
            }
          },
          (err: string) => {
            cancelStreamRef.current = null;
            setError(err);
            setGeneratingPosts(false);
          },
        );

        cancelStreamRef.current = cancel;
        return;
      }

      // If pipeline returned QUEUE_UNAVAILABLE, fall through to synchronous
      if (pipelineResult.error?.code !== 'QUEUE_UNAVAILABLE') {
        setError(pipelineResult.error?.message || 'Failed to start pipeline');
        setGeneratingPosts(false);
        return;
      }
    } catch {
      // Pipeline not available, fall through to synchronous
    }

    // Synchronous fallback (no Redis)
    try {
      if (sourceIds && sourceIds.length > 0) {
        await Promise.all(sourceIds.map(id => api.syncSource(id)));
      } else {
        const unprocessedSources = sources.filter(s => !s.last_synced_at);
        if (unprocessedSources.length > 0) {
          await Promise.all(unprocessedSources.map(source => api.syncSource(source.id)));
        }
      }

      const result = await api.generateBatchPosts(count, sourceIds, context, sourceItemName);

      if (result.error) {
        if (result.error.code === 'NO_KNOWLEDGE') {
          await Promise.all(sources.map(source => api.syncSource(source.id)));
          const retryResult = await api.generateBatchPosts(count, sourceIds, context, sourceItemName);
          if (retryResult.error) {
            setError(retryResult.error.message);
            setGeneratingPosts(false);
            return;
          }
        } else if (result.error.code === 'MISSING_VOICE_PROFILE') {
          setError('Please complete onboarding first to set up your voice profile.');
          setGeneratingPosts(false);
          return;
        } else {
          setError(result.error.message);
          setGeneratingPosts(false);
          return;
        }
      }

      clearCacheKey('posts-list');
      clearCacheKey('posts-stats');
      sessionStorage.setItem('mazo_postsGeneratedAt', String(Date.now()));
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate posts');
      setGeneratingPosts(false);
    }
  };

  const startEdit = (source: Source) => {
    setEditingSource(source);
    setNoteName(source.name);
    setNoteContent(source.content || '');
  };

  const cancelEdit = () => {
    setEditingSource(null);
    setShowAddNote(false);
    setNoteName('');
    setNoteContent('');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSelectedFilesCount = (source: Source) => {
    const config = source.config as { selectedFiles?: DriveFile[] } | undefined;
    return config?.selectedFiles?.length || 0;
  };

  // Generic integration API key modal — rendered in both empty-state and main render paths
  const integrationModal = pendingIntegration && (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Connect {pendingIntegration.name}</h3>
          <button
            onClick={() => { setPendingIntegration(null); setIntegrationApiKey(''); setIntegrationApiSecret(''); }}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{pendingIntegration.helpText}</p>
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {pendingIntegration.keyLabel}
          </label>
          <input
            type="password"
            value={integrationApiKey}
            onChange={(e) => setIntegrationApiKey(e.target.value)}
            placeholder={pendingIntegration.keyPlaceholder}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400 text-sm"
            onKeyDown={(e) => e.key === 'Enter' && !pendingIntegration.secondKeyLabel && handleSubmitIntegration()}
          />
        </div>
        {pendingIntegration.secondKeyLabel && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {pendingIntegration.secondKeyLabel}
            </label>
            <input
              type="password"
              value={integrationApiSecret}
              onChange={(e) => setIntegrationApiSecret(e.target.value)}
              placeholder={pendingIntegration.secondKeyPlaceholder}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitIntegration()}
            />
          </div>
        )}
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => { setPendingIntegration(null); setIntegrationApiKey(''); setIntegrationApiSecret(''); setError(null); }}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitIntegration}
            disabled={!integrationApiKey.trim() || (!!pendingIntegration.secondKeyLabel && !integrationApiSecret.trim()) || connectingIntegration}
            className="px-4 py-2 text-sm text-white bg-mazo-500 rounded-lg hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {connectingIntegration && <Loader2 className="w-4 h-4 animate-spin" />}
            Connect
          </button>
        </div>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Please sign in to manage sources.</p>
      </div>
    );
  }

  // Show welcome hero when no sources exist
  if (!loading && sources.length === 0) {
    return (
      <div className="space-y-6">
        <WelcomeHero
          onConnectSources={() => setShowConnectorModal(true)}
          onAddManualNote={() => setShowAddNote(true)}
        />

        {/* Connector Modal */}
        <ConnectorModal
          isOpen={showConnectorModal}
          onClose={() => setShowConnectorModal(false)}
          onConnectGoogleDrive={handleConnectGoogleDrive}
          onConnectGoogleMeet={handleConnectGoogleMeet}
          connectingGoogle={connectingGoogle}
          connectingGoogleMeet={connectingGoogleMeet}
          connectingGranola={connectingGranola}
          onConnectGranolaOAuth={handleConnectGranolaOAuth}
          connectingOtter={connectingOtter}
          onConnectOtterOAuth={handleConnectOtterOAuth}
          onConnectTldv={() => {
            setShowConnectorModal(false);
            setShowTldvApiKeyInput(true);
          }}
          onAddManualNote={() => {
            setShowConnectorModal(false);
            setShowAddNote(true);
          }}
          onConnectIntegration={handleConnectIntegration}
        />

        {integrationModal}

        {/* Source Content Preview Dialog */}
        <Dialog
          open={previewLoading || previewContent !== null}
          onOpenChange={(open: boolean) => {
            if (!open) {
              setPreviewContent(null);
              setPreviewLoading(false);
            }
          }}
        >
          <DialogContent className="pc-dialog--wide" style={{ maxWidth: '48rem', maxHeight: '80vh' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                {previewTitle || 'Preview'}
              </DialogTitle>
              <DialogClose />
            </DialogHeader>
            <DialogBody>
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-sm text-gray-700 max-h-[60vh] overflow-y-auto leading-relaxed">
                  {previewContent}
                </div>
              )}
            </DialogBody>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Note Dialog (empty state) */}
        <Dialog
          open={showAddNote || !!editingSource}
          onOpenChange={(open: boolean) => {
            if (!open) cancelEdit();
          }}
        >
          <DialogContent className="pc-dialog--wide" style={{ maxWidth: '48rem' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                {editingSource ? 'Edit Note' : 'Add Note'}
              </DialogTitle>
              <DialogClose />
            </DialogHeader>
            <DialogBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={noteName}
                    onChange={(e) => setNoteName(e.target.value)}
                    placeholder="e.g., My thoughts on leadership"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Write your thoughts, insights, or paste existing content..."
                    rows={14}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">Supports Markdown</p>
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <button
                onClick={cancelEdit}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={editingSource ? handleUpdateNote : handleCreateNote}
                disabled={!noteName.trim()}
                className="px-4 py-2 bg-mazo-500 text-white rounded-lg hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingSource ? 'Save Changes' : 'Create Note'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* tldv API Key Input — must be here for empty-state flow */}
        {showTldvApiKeyInput && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <TldvLogo className="w-8 h-8" />
                  <h3 className="text-lg font-semibold text-gray-900">Connect tl;dv</h3>
                </div>
                <button
                  onClick={() => { setShowTldvApiKeyInput(false); setTldvApiKey(''); }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Enter your tl;dv API key to sync meeting transcripts. You can find your API key in your{' '}
                <a
                  href="https://tldv.io/app/settings/integrations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-mazo-600 hover:text-mazo-800 underline"
                >
                  tl;dv settings
                </a>.
              </p>
              <input
                type="password"
                value={tldvApiKey}
                onChange={(e) => setTldvApiKey(e.target.value)}
                placeholder="Enter your tl;dv API key"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400 mb-4"
                onKeyDown={(e) => { if (e.key === 'Enter') handleConnectTldv(); }}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowTldvApiKeyInput(false); setTldvApiKey(''); }}
                  className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectTldv}
                  disabled={!tldvApiKey.trim() || connectingTldv}
                  className="flex-1 py-2.5 px-4 bg-mazo-500 text-white rounded-lg font-medium hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {connectingTldv ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Loading overlay rendered via portal — with real-time progress when available
  const loadingOverlay = generatingPosts ? createPortal(
    <div
      id="generating-posts-overlay"
      data-testid="loading-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
      }}
    >
      <div className="text-center space-y-6">
        <div className="relative mx-auto w-24 h-24">
          <div className="absolute inset-0 rounded-full border-4 border-mazo-200 animate-pulse"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-mazo-500 animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="p-4 bg-gradient-to-br from-mazo-400 to-mazo-600 rounded-full shadow-lg">
              <img src="/logo-white.svg" alt="" className="h-8 w-8 animate-pulse" />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-gray-900">
            {progressPhase === 'syncing' ? 'Reading Your Sources' :
             progressPhase === 'generating' ? 'Writing Your Posts' :
             'Getting Started...'}
          </h3>
          <p className="text-gray-600 max-w-sm mx-auto">
            {progressPhase === 'syncing' && progressData
              ? `Extracting insights from source ${progressData.syncCompleted + 1} of ${progressData.syncTotal}...`
              : progressPhase === 'generating' && progressData
              ? `Crafting post ${progressData.generateCompleted + 1} of ${progressData.generateTotal}...`
              : 'Preparing to create LinkedIn posts from your knowledge sources...'}
          </p>
          <p className="text-sm text-gray-400 max-w-sm mx-auto mt-2">
            {progressPhase === 'syncing'
              ? 'First-time import takes a bit longer while we deeply analyze your content. Future runs will be much faster!'
              : progressPhase === 'generating'
              ? 'Almost there — writing posts in your unique voice...'
              : 'This will take less time than brewing your coffee. Sit tight!'}
          </p>
        </div>

        {/* Progress bar */}
        {progressData && (progressData.syncTotal > 0 || progressData.generateTotal > 0) && (
          <div className="w-64 mx-auto">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-mazo-400 to-mazo-500 rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${Math.round(
                    ((progressData.syncCompleted + progressData.generateCompleted) /
                      Math.max(progressData.syncTotal + progressData.generateTotal, 1)) * 100
                  )}%`,
                }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {Math.round(
                ((progressData.syncCompleted + progressData.generateCompleted) /
                  Math.max(progressData.syncTotal + progressData.generateTotal, 1)) * 100
              )}% complete
            </p>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-sm text-mazo-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="animate-pulse">
            {progressPhase === 'syncing' ? 'Deeply reading and understanding your content...'
              : progressPhase === 'generating' ? 'Crafting posts in your unique voice...'
              : 'Warming up the engines...'}
          </span>
        </div>
        {!progressData && (
          <div className="flex justify-center gap-2 pt-2">
            <div className="w-2 h-2 rounded-full bg-mazo-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 rounded-full bg-mazo-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 rounded-full bg-mazo-300 animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  // Post count dialog
  const postCountDialog = showPostCountDialog ? createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Generate Posts</h3>
          <button
            onClick={() => { sessionStorage.removeItem('mazo_pendingPostCount'); sessionStorage.removeItem('mazo_pendingSourceIds'); sessionStorage.removeItem('mazo_pendingSourceItemName'); setShowPostCountDialog(false); setUseSelectedSources(false); setSelectedSourceIds([]); setPendingSourceItemName(null); setGenerationPerspective(''); setGenerationLanguage('en'); setGenerationAdditionalContext(''); }}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {pendingFileCount > 0 && (
          <p className="text-sm text-gray-500 mb-3">
            {pendingFileCount} item{pendingFileCount !== 1 ? 's' : ''} selected
          </p>
        )}

        {/* Writing perspective */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Writing perspective</label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: '', label: 'As myself' },
              { value: 'my company', label: 'As my company' },
              { value: 'my clients', label: 'As my clients' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setGenerationPerspective(option.value)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                  generationPerspective === option.value
                    ? 'border-mazo-400 bg-mazo-50 text-mazo-700 font-medium'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {/* Custom perspective input shown when none of the presets match */}
          {generationPerspective !== '' && generationPerspective !== 'my company' && generationPerspective !== 'my clients' && (
            <input
              type="text"
              value={generationPerspective}
              onChange={(e) => setGenerationPerspective(e.target.value.slice(0, 200))}
              className="mt-2 w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
              placeholder="Custom perspective..."
            />
          )}
          <button
            onClick={() => setGenerationPerspective(
              generationPerspective !== '' && generationPerspective !== 'my company' && generationPerspective !== 'my clients'
                ? '' : 'custom'
            )}
            className="mt-1.5 text-xs text-mazo-500 hover:text-mazo-600"
          >
            {generationPerspective !== '' && generationPerspective !== 'my company' && generationPerspective !== 'my clients'
              ? 'Use default' : 'Custom perspective...'}
          </button>
        </div>

        {/* Output language */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Post language</label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'en', label: 'English' },
              { value: 'original', label: 'Original conversation language' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setGenerationLanguage(option.value)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                  generationLanguage === option.value
                    ? 'border-mazo-400 bg-mazo-50 text-mazo-700 font-medium'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Additional context */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Additional context <span className="text-gray-400 font-normal">(optional)</span></label>
          <textarea
            value={generationAdditionalContext}
            onChange={(e) => setGenerationAdditionalContext(e.target.value.slice(0, 200))}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-mazo-500 focus:border-transparent resize-none"
            rows={2}
            placeholder="e.g., Focus on leadership lessons, Write for a tech audience..."
            maxLength={200}
          />
          <div className="text-right text-xs text-gray-400 mt-0.5">
            {generationAdditionalContext.length}/200
          </div>
        </div>

        {/* Post count selection */}
        <div className="mb-1">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Number of posts</label>
          <div className="flex gap-3">
            {[1, 3, 5, 10].map((count) => (
              <button
                key={count}
                onClick={() => handlePostCountSelect(count, useSelectedSources ? selectedSourceIds : undefined)}
                className="flex-1 py-3 px-4 border-2 border-gray-200 rounded-lg text-center font-semibold text-gray-700 hover:border-mazo-400 hover:text-mazo-500 hover:bg-mazo-50 transition-all"
              >
                {count}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {loadingOverlay}
      {postCountDialog}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sources</h1>
            <p className="text-gray-500 mt-1">
              {new Set(sources.map(s => s.type)).size} source{new Set(sources.map(s => s.type)).size !== 1 ? 's' : ''} connected
            </p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">
              Dismiss
            </button>
          </div>
        )}

        {(loading || waitingForIntegration) ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-400" />
          </div>
        ) : (
          <>
            {/* Back button when drilling into a source */}
            {selectedSourceType && (
              <button
                onClick={() => setSelectedSourceType(null)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 group"
              >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
                Back to all sources
              </button>
            )}

            {/* Section A: Source Connections */}
            {!selectedSourceType && (
            <div>
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Source Connections</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sources.filter((s, i, arr) => {
                  // Show one card per connection type
                  return arr.findIndex(x => x.type === s.type) === i;
                }).map((source) => {
                  const isGoogleDrive = source.type === 'google_drive';
                  const isGoogleMeet = source.type === 'google_meet';
                  const isTldv = source.type === 'tldv';
                  const isNote = source.type === 'custom_note';
                  const hasPickerConfig = PICKER_CONFIG[source.type] !== undefined;
                  const noteCount = isNote ? sources.filter(s => s.type === 'custom_note').length : 0;

                  // Subtitle for each source type
                  const pickerItemCount = hasPickerConfig ? getSelectedItemsCount(source) : 0;
                  const pickerLabel = hasPickerConfig ? PICKER_CONFIG[source.type].itemLabel : '';
                  const subtitle = isGoogleDrive
                    ? `${getSelectedFilesCount(source)} files selected`
                    : isGoogleMeet
                    ? `${getSelectedFilesCount(source)} recordings selected`
                    : isTldv
                    ? `${getSelectedMeetingsCount(source)} meetings selected`
                    : isNote
                    ? `${noteCount} note${noteCount !== 1 ? 's' : ''}`
                    : hasPickerConfig
                    ? `${pickerItemCount} ${pickerLabel}${pickerItemCount !== 1 ? 's' : ''} selected`
                    : 'Connected';

                  return (
                    <div
                      key={`connection-${source.type}`}
                      onClick={() => setSelectedSourceType(source.type)}
                      className="bg-white border rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {isNote ? (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                              {getSourceIcon(source.type, 'h-5 w-5')}
                            </div>
                          ) : (
                            getSourceIcon(source.type, 'w-10 h-10')
                          )}
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {getSourceLabel(source.type)}
                            </h3>
                            <p className="text-xs text-gray-500">{subtitle}</p>
                          </div>
                        </div>
                        <CheckCircle className="h-5 w-5 text-mazo-500 flex-shrink-0" />
                      </div>

                      {/* Card actions */}
                      <div className="flex items-center gap-2 pt-3 border-t">
                        {isGoogleDrive ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowDrivePicker(true); }}
                            className="text-xs px-3 py-1.5 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                          >
                            Select Files
                          </button>
                        ) : isGoogleMeet ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowMeetDrivePicker(true); }}
                            className="text-xs px-3 py-1.5 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                          >
                            Select Recordings
                          </button>
                        ) : isTldv ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowTldvPicker(true); }}
                            className="text-xs px-3 py-1.5 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                          >
                            Select Meetings
                          </button>
                        ) : isNote ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowAddNote(true); }}
                            className="text-xs px-3 py-1.5 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                          >
                            Add Note
                          </button>
                        ) : hasPickerConfig ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActivePickerSourceType(source.type); setShowIntegrationPicker(true); }}
                            className="text-xs px-3 py-1.5 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                          >
                            {PICKER_CONFIG[source.type].actionLabel}
                          </button>
                        ) : null}
                        {!isNote && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setUseSelectedSources(true);
                              setSelectedSourceIds([source.id]);
                              sessionStorage.setItem('mazo_pendingSourceIds', JSON.stringify([source.id]));
                              setPendingSourceItemName(null);
                              sessionStorage.removeItem('mazo_pendingSourceItemName');
                              sessionStorage.setItem('mazo_pendingPostCount', '1');
                              setPendingFileCount(1);
                              setShowPostCountDialog(true);
                            }}
                            className={`text-xs px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 ${
                              source.last_synced_at
                                ? 'text-gray-400 hover:text-mazo-600 hover:bg-gray-50 border border-gray-200'
                                : 'text-mazo-600 hover:text-mazo-700 hover:bg-mazo-50'
                            }`}
                            title="Create posts from this source"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            {source.last_synced_at ? 'More Posts' : 'Create Posts'}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isNote) {
                              handleDeleteSource(source.id);
                            } else if (isGoogleMeet) {
                              handleDisconnectGoogleMeet();
                            } else if (isTldv) {
                              handleDisconnectTldv();
                            } else if (hasPickerConfig) {
                              handleDisconnectIntegration(source.type);
                            } else {
                              handleDeleteSource(source.id);
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-auto"
                          title={isNote ? "Delete" : "Disconnect"}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Add Another Source card */}
                <button
                  onClick={() => setShowConnectorModal(true)}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-5 flex flex-col items-center justify-center gap-3 hover:border-mazo-500 hover:bg-mazo-50 hover:shadow-sm transition-all min-h-[140px] group cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-full bg-mazo-50 border border-mazo-200 flex items-center justify-center group-hover:bg-mazo-100 transition-colors">
                    <Plus className="h-6 w-6 text-mazo-500 group-hover:text-mazo-600 transition-colors" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-gray-700 group-hover:text-mazo-700">Add Source</p>
                    <p className="text-xs text-gray-400 mt-0.5">Google Drive, tl;dv, Notes, and more</p>
                  </div>
                </button>
              </div>
            </div>
            )}

            {/* Section B: Linked Documents */}
            {(() => {
              // Build document rows from all sources
              const documentRows: Array<{
                key: string;
                name: string;
                sourceType: string;
                sourceId: string;
                lastSyncedAt?: string;
              }> = [];

              sources.forEach(source => {
                if (source.type === 'google_drive' || source.type === 'google_meet') {
                  const config = source.config as { selectedFiles?: DriveFile[] } | undefined;
                  const files = config?.selectedFiles || [];
                  files.forEach(file => {
                    documentRows.push({
                      key: `${source.type}-${file.id}`,
                      name: file.name,
                      sourceType: source.type,
                      sourceId: source.id,
                      lastSyncedAt: source.last_synced_at,
                    });
                  });
                } else if (source.type === 'tldv') {
                  const config = source.config as { selectedMeetings?: TldvMeeting[] } | undefined;
                  const meetings = config?.selectedMeetings || [];
                  meetings.forEach(meeting => {
                    documentRows.push({
                      key: `tldv-${meeting.id}`,
                      name: meeting.name,
                      sourceType: 'tldv',
                      sourceId: source.id,
                      lastSyncedAt: source.last_synced_at,
                    });
                  });
                } else if (source.type === 'custom_note') {
                  documentRows.push({
                    key: `note-${source.id}`,
                    name: source.name,
                    sourceType: 'custom_note',
                    sourceId: source.id,
                    lastSyncedAt: source.last_synced_at,
                  });
                } else if (PICKER_CONFIG[source.type]) {
                  // Integration types with pickers — show individual selected items
                  const config = source.config as { selectedItems?: PickerItem[] } | undefined;
                  const items = config?.selectedItems || [];
                  if (items.length > 0) {
                    items.forEach(item => {
                      documentRows.push({
                        key: `${source.type}-${source.id}-${item.id}`,
                        name: item.name,
                        sourceType: source.type,
                        sourceId: source.id,
                        lastSyncedAt: source.last_synced_at,
                      });
                    });
                  }
                  // Skip sources with 0 selected items — no documents to show
                } else {
                  // Other integration types without pickers
                  documentRows.push({
                    key: `${source.type}-${source.id}`,
                    name: source.name,
                    sourceType: source.type,
                    sourceId: source.id,
                    lastSyncedAt: source.last_synced_at,
                  });
                }
              });

              // Filter by selected source type if drilling down
              const filteredRows = selectedSourceType
                ? documentRows.filter(row => row.sourceType === selectedSourceType)
                : documentRows;

              if (filteredRows.length === 0 && !selectedSourceType) return null;

              const allChecked = filteredRows.length > 0 && filteredRows.every(r => checkedDocKeys.has(r.key));

              const handleSelectAll = () => {
                if (allChecked) {
                  setCheckedDocKeys(new Set());
                } else {
                  setCheckedDocKeys(new Set(filteredRows.map(r => r.key)));
                }
              };

              const handleToggleRow = (key: string) => {
                setCheckedDocKeys(prev => {
                  const next = new Set(prev);
                  if (next.has(key)) {
                    next.delete(key);
                  } else {
                    next.add(key);
                  }
                  return next;
                });
              };

              const handleInlineGenerate = () => {
                if (generatingPosts || checkedDocKeys.size === 0) return;

                const checkedRows = documentRows.filter(r => checkedDocKeys.has(r.key));
                const derivedIds = [...new Set(checkedRows.map(r => r.sourceId))];

                // Multi-select always works at source level, clear any item-level filter
                setPendingSourceItemName(null);
                sessionStorage.removeItem('mazo_pendingSourceItemName');

                if (allChecked) {
                  setUseSelectedSources(false);
                  sessionStorage.removeItem('mazo_pendingSourceIds');
                } else {
                  setUseSelectedSources(true);
                  setSelectedSourceIds(derivedIds);
                  sessionStorage.setItem('mazo_pendingSourceIds', JSON.stringify(derivedIds));
                }

                sessionStorage.setItem('mazo_pendingPostCount', String(checkedDocKeys.size));
                setPendingFileCount(checkedDocKeys.size);
                setShowPostCountDialog(true);
              };

              const exitSelectionMode = () => {
                setSelectionMode(false);
                setCheckedDocKeys(new Set());
              };

              return (
                <div>
                  {/* Header with title and multi-select button */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Linked Documents</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectionMode
                          ? 'Select documents to generate posts from'
                          : 'Click a document to preview its content'}
                      </p>
                    </div>
                    {!selectionMode && (
                      <button
                        onClick={() => setSelectionMode(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm transition-all flex-shrink-0"
                      >
                        <Sparkles className="h-4 w-4 text-mazo-500" />
                        Create Posts from Multiple Sources
                      </button>
                    )}
                  </div>
                  <div className="bg-white border rounded-xl overflow-visible">
                    {/* Table header */}
                    {selectionMode ? (
                      <div className="grid grid-cols-[32px_1fr_110px] gap-3 px-5 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wide items-center">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-mazo-400 bg-gray-100 border-gray-300 rounded focus:ring-mazo-400 cursor-pointer"
                          />
                        </div>
                        <div>Document</div>
                        <div>Last Synced</div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-[1fr_110px_auto] gap-3 px-5 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wide items-center">
                        <div>Document</div>
                        <div>Last Synced</div>
                        <div className="text-right">Actions</div>
                      </div>
                    )}
                    {/* Table rows */}
                    <div className="divide-y">
                      {filteredRows.map(row => {
                        const isChecked = checkedDocKeys.has(row.key);
                        const isMenuOpen = openMenuKey === row.key;

                        if (selectionMode) {
                          // Selection mode: checkbox + document + last synced (no actions)
                          return (
                            <div
                              key={row.key}
                              onClick={() => handleToggleRow(row.key)}
                              className={`grid grid-cols-[32px_1fr_110px] gap-3 px-5 py-3 items-center transition-colors cursor-pointer ${
                                isChecked ? 'bg-mazo-50/50' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleToggleRow(row.key)}
                                  className="w-4 h-4 text-mazo-400 bg-gray-100 border-gray-300 rounded focus:ring-mazo-400 cursor-pointer"
                                />
                              </div>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="flex-shrink-0">{getSourceIcon(row.sourceType, 'w-4 h-4')}</span>
                                <span className="text-sm text-gray-900 truncate">{row.name}</span>
                              </div>
                              <div className="text-xs text-gray-400">
                                {formatDate(row.lastSyncedAt)}
                              </div>
                            </div>
                          );
                        }

                        // Normal mode: clickable row for preview, actions column
                        return (
                          <div
                            key={row.key}
                            onClick={() => handlePreviewSource(row.sourceId, row.name, row.name)}
                            className="grid grid-cols-[1fr_110px_auto] gap-3 px-5 py-3 items-center transition-colors hover:bg-gray-50 cursor-pointer"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="flex-shrink-0">{getSourceIcon(row.sourceType, 'w-4 h-4')}</span>
                              <span className="text-sm text-gray-900 truncate">{row.name}</span>
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatDate(row.lastSyncedAt)}
                            </div>
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              {/* Primary: Create Posts */}
                              <button
                                onClick={() => {
                                  setUseSelectedSources(true);
                                  setSelectedSourceIds([row.sourceId]);
                                  sessionStorage.setItem('mazo_pendingSourceIds', JSON.stringify([row.sourceId]));
                                  setPendingSourceItemName(row.name);
                                  sessionStorage.setItem('mazo_pendingSourceItemName', row.name);
                                  sessionStorage.setItem('mazo_pendingPostCount', '1');
                                  setPendingFileCount(1);
                                  setShowPostCountDialog(true);
                                }}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium text-white bg-mazo-500 hover:bg-mazo-400 transition-colors"
                              >
                                <Sparkles className="h-3 w-3" />
                                Create Posts
                              </button>
                              {/* Secondary: View Posts */}
                              <button
                                onClick={() => navigate(`/?source=${row.sourceId}&source_item=${encodeURIComponent(row.name)}`)}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1.5 text-gray-500 hover:text-gray-700 rounded transition-colors"
                              >
                                View Posts
                              </button>
                              {/* More actions dropdown */}
                              <div className="relative">
                                <button
                                  onClick={() => setOpenMenuKey(isMenuOpen ? null : row.key)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </button>
                                {isMenuOpen && (
                                  <>
                                    {/* Backdrop to close menu */}
                                    <div className="fixed inset-0 z-10" onClick={() => setOpenMenuKey(null)} />
                                    <div className="absolute right-0 bottom-full mb-1 w-40 bg-white border rounded-lg shadow-lg z-20 py-1">
                                      <button
                                        onClick={() => {
                                          setOpenMenuKey(null);
                                          handleSync(row.sourceId);
                                        }}
                                        disabled={syncing.has(row.sourceId)}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                      >
                                        <RefreshCw className={`h-3.5 w-3.5 ${syncing.has(row.sourceId) ? 'animate-spin' : ''}`} />
                                        Refresh
                                      </button>
                                      {row.sourceType === 'custom_note' && (
                                        <button
                                          onClick={() => {
                                            setOpenMenuKey(null);
                                            const noteSource = sources.find(s => s.id === row.sourceId);
                                            if (noteSource) startEdit(noteSource);
                                          }}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                          <Edit2 className="h-3.5 w-3.5" />
                                          Edit
                                        </button>
                                      )}
                                      <button
                                        onClick={() => {
                                          setOpenMenuKey(null);
                                          if (row.sourceType === 'custom_note') {
                                            handleDeleteSource(row.sourceId);
                                          } else if (row.sourceType === 'google_meet') {
                                            handleDisconnectGoogleMeet();
                                          } else if (row.sourceType === 'tldv') {
                                            handleDisconnectTldv();
                                          } else if (PICKER_CONFIG[row.sourceType]) {
                                            handleDisconnectIntegration(row.sourceType);
                                          } else {
                                            handleDeleteSource(row.sourceId);
                                          }
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        Delete
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Floating action bar in selection mode */}
                  {selectionMode && createPortal(
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-white border rounded-xl shadow-lg">
                      {checkedDocKeys.size > 0 ? (
                        <button
                          onClick={handleInlineGenerate}
                          disabled={generatingPosts}
                          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-mazo-500 hover:bg-mazo-400 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {generatingPosts ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4" />
                              Generate from Selected ({checkedDocKeys.size})
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="text-sm text-gray-500 px-2">Select documents to generate posts</span>
                      )}
                      <button
                        onClick={exitSelectionMode}
                        className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>,
                    document.body
                  )}
                </div>
              );
            })()}
          </>
        )}

        {/* Google Drive Picker Modal */}
        {showDrivePicker && googleDriveSource && (() => {
          const config = googleDriveSource.config as { selectedFiles?: DriveFile[] } | undefined;
          const selectedFiles = config?.selectedFiles || [];
          return (
            <GoogleDrivePicker
              onClose={() => setShowDrivePicker(false)}
              onSave={handleSaveDriveSelection}
              initialSelected={selectedFiles}
            />
          );
        })()}

        {/* Google Meet Drive Picker Modal */}
        {showMeetDrivePicker && googleMeetSource && (() => {
          const config = googleMeetSource.config as { selectedFiles?: DriveFile[] } | undefined;
          const selectedFiles = config?.selectedFiles || [];
          return (
            <GoogleDrivePicker
              sourceType="google_meet"
              onClose={() => setShowMeetDrivePicker(false)}
              onSave={handleSaveMeetDriveSelection}
              initialSelected={selectedFiles}
            />
          );
        })()}

        {/* tldv API Key Input */}
        {showTldvApiKeyInput && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <TldvLogo className="w-8 h-8" />
                  <h3 className="text-lg font-semibold text-gray-900">Connect tl;dv</h3>
                </div>
                <button
                  onClick={() => { setShowTldvApiKeyInput(false); setTldvApiKey(''); }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Enter your tl;dv API key to sync meeting transcripts. You can find your API key in your{' '}
                <a
                  href="https://tldv.io/app/settings/integrations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-mazo-600 hover:text-mazo-800 underline"
                >
                  tl;dv settings
                </a>.
              </p>
              <input
                type="password"
                value={tldvApiKey}
                onChange={(e) => setTldvApiKey(e.target.value)}
                placeholder="Enter your tl;dv API key"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400 mb-4"
                onKeyDown={(e) => { if (e.key === 'Enter') handleConnectTldv(); }}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowTldvApiKeyInput(false); setTldvApiKey(''); }}
                  className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectTldv}
                  disabled={!tldvApiKey.trim() || connectingTldv}
                  className="flex-1 py-2.5 px-4 bg-mazo-500 text-white rounded-lg font-medium hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {connectingTldv ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* tldv Meeting Picker Modal */}
        {showTldvPicker && tldvSource && (() => {
          const config = tldvSource.config as { selectedMeetings?: TldvMeeting[] } | undefined;
          const selectedMeetings = config?.selectedMeetings || [];
          return (
            <TldvMeetingPicker
              onClose={() => setShowTldvPicker(false)}
              onSave={handleSaveTldvSelection}
              initialSelected={selectedMeetings}
            />
          );
        })()}

        {/* Integration Item Picker Modal (Notion/Fathom/Gong/Fireflies/Avoma) */}
        {showIntegrationPicker && activePickerSourceType && PICKER_CONFIG[activePickerSourceType] && (() => {
          const sourceForPicker = sources.find(s => s.type === activePickerSourceType);
          const config = sourceForPicker?.config as { selectedItems?: PickerItem[] } | undefined;
          const initialSelected = config?.selectedItems || [];
          const pickerConfig = PICKER_CONFIG[activePickerSourceType];
          return (
            <IntegrationItemPicker
              title={pickerConfig.title}
              itemLabel={pickerConfig.itemLabel}
              sourceType={activePickerSourceType}
              onClose={() => { setShowIntegrationPicker(false); setActivePickerSourceType(null); }}
              onSave={handleSaveIntegrationSelection}
              initialSelected={initialSelected}
              fetchItems={async (params) => {
                const result = await api.listIntegrationItems(activePickerSourceType, params);
                if (result.data?.data) {
                  return result.data.data;
                }
                throw new Error(result.error?.message || 'Failed to fetch items');
              }}
            />
          );
        })()}

        {/* Connector Modal */}
        <ConnectorModal
          isOpen={showConnectorModal}
          onClose={() => setShowConnectorModal(false)}
          onConnectGoogleDrive={handleConnectGoogleDrive}
          onConnectGoogleMeet={handleConnectGoogleMeet}
          connectingGoogle={connectingGoogle}
          connectingGoogleMeet={connectingGoogleMeet}
          connectingGranola={connectingGranola}
          onConnectGranolaOAuth={handleConnectGranolaOAuth}
          connectingOtter={connectingOtter}
          onConnectOtterOAuth={handleConnectOtterOAuth}
          onConnectTldv={() => {
            setShowConnectorModal(false);
            setShowTldvApiKeyInput(true);
          }}
          onAddManualNote={() => {
            setShowConnectorModal(false);
            setShowAddNote(true);
          }}
          onConnectIntegration={handleConnectIntegration}
        />

        {integrationModal}

        {/* Source Content Preview Dialog */}
        <Dialog
          open={previewLoading || previewContent !== null}
          onOpenChange={(open: boolean) => {
            if (!open) {
              setPreviewContent(null);
              setPreviewLoading(false);
            }
          }}
        >
          <DialogContent className="pc-dialog--wide" style={{ maxWidth: '48rem', maxHeight: '80vh' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                {previewTitle || 'Preview'}
              </DialogTitle>
              <DialogClose />
            </DialogHeader>
            <DialogBody>
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-sm text-gray-700 max-h-[60vh] overflow-y-auto leading-relaxed">
                  {previewContent}
                </div>
              )}
            </DialogBody>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Note Dialog */}
        <Dialog
          open={showAddNote || !!editingSource}
          onOpenChange={(open: boolean) => {
            if (!open) cancelEdit();
          }}
        >
          <DialogContent className="pc-dialog--wide" style={{ maxWidth: '48rem' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                {editingSource ? 'Edit Note' : 'Add Note'}
              </DialogTitle>
              <DialogClose />
            </DialogHeader>
            <DialogBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={noteName}
                    onChange={(e) => setNoteName(e.target.value)}
                    placeholder="e.g., My thoughts on leadership"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Write your thoughts, insights, or paste existing content..."
                    rows={14}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-mazo-400 focus:border-mazo-400 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">Supports Markdown</p>
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <button
                onClick={cancelEdit}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={editingSource ? handleUpdateNote : handleCreateNote}
                disabled={!noteName.trim()}
                className="px-4 py-2 bg-mazo-500 text-white rounded-lg hover:bg-mazo-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingSource ? 'Save Changes' : 'Create Note'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
